import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { removeObject } from "find-and";
import { debounce } from "@evergis/sgis/es/utils/utils";
import { IconToggle, IOption, Menu, Popup } from "@evergis/ui";
import { IconTypesKeys } from "@evergis/ui/dist/atoms/Icon";
import { Permissions } from "@evergis/api";
import { getTempSourceLayers } from "../../../../ducks/tempSourceLayers/selectors";

import { DeleteButton, ExpandButton, Header, SearchButton, SearchMenu, SelectionButton } from "../../styled";
import { SearchField, SearchMenuContainer } from "./styled";
import { AddLayer } from "../AddLayer";
import { LayersListSearchResults } from "./LayersListSearchResults";
import { getProject } from "../../../../ducks/project/selectors";
import { getSearchIcon } from "./utils/getSearchIcon";
import { useProjectInfo, useProjectUpdate } from "../../../../ducks/project/hooks";
import { useProjectLayerInfos } from "../../../../hooks/project/useProjectLayers";
import { useLayersSearchContext } from "./context";
import { useToggle } from "../../../../hooks/useToggle";
import { useIsShared } from "../../../../hooks/api/useIsShared";
import { api } from "../../../../evergis/api";
import { TaskDataType } from "../../../../interfaces/scheduler";
import { LayerListSearchProps, SearchMode } from "./types";

const DEBOUNCE_DELAY = 500;
const LAYER_TYPES = ["PostgresLayerService", "PostgresTileLayerService", "QueryLayerService"];

export const LayerListSearch: FC<LayerListSearchProps> = ({
  searchMode,
  searchFilter,
  selected,
  isDisabled,
  setSearchFilter,
  setSearchMode,
}) => {
  const isShared = useIsShared();
  const { t } = useTranslation("layerList");
  const [{ name: projectName, content }] = useProjectInfo();
  const { layerInfos } = useProjectLayerInfos();
  const tempSourceLayers = useSelector(getTempSourceLayers);
  const { setTaskId, setSearchFilter: setContextSearch } = useLayersSearchContext();
  const { permissions: projectPermissions } = useSelector(getProject);
  const [isFocused, setIsFocused] = useState(false);
  const [showOptions, toggleOptions] = useToggle();
  const inputRef = useRef<HTMLElement>();

  const AddLayerButton = useCallback(
    ({ innerRef, toggleMenu }) => (
      <IconToggle
        title={t("addLayer")}
        disabled={isDisabled || !projectName}
        kind={"feature_add"}
        innerRef={innerRef}
        onClick={toggleMenu}
      />
    ),
    [t, isDisabled, projectName],
  );

  const searchAll = useCallback(
    async (searchValue: string) => {
      if (!searchValue) {
        return;
      }

      const params = {
        sources: [...layerInfos, ...tempSourceLayers]
          .filter(({ type }) => LAYER_TYPES.includes(type))
          .map(({ name }) => ({
            type: TaskDataType.Layer,
            serviceName: name,
          })),
        filter: searchValue,
      };

      setContextSearch(searchValue);

      const { taskId } = await api.scheduler.universalSearchTask(params);

      setTaskId(taskId);
    },
    [layerInfos, setContextSearch, setTaskId, tempSourceLayers],
  );

  const selectOption = useCallback(
    (items: IOption[]) => {
      setSearchMode(items[0].value as SearchMode);
      toggleOptions();
    },
    [setSearchMode, toggleOptions],
  );

  const onFocus = useCallback(() => {
    setIsFocused(true);

    if (!searchMode || searchMode === SearchMode.Selection) {
      setSearchMode(SearchMode.Layers);
    }
  }, [searchMode, setSearchMode]);

  const onBlur = useCallback(() => {
    setIsFocused(false);

    if (!searchFilter) {
      setSearchMode(null);
    }
  }, [searchFilter, setSearchMode]);

  const toggleSelectionMode = useCallback(() => {
    setSearchMode(searchMode === SearchMode.Selection ? null : SearchMode.Selection);
  }, [searchMode, setSearchMode]);

  const updateProject = useProjectUpdate();

  const onDelete = useCallback(() => {
    let newItems = [...content?.items];

    selected.forEach(selectedItem => {
      newItems = removeObject(newItems, { name: selectedItem });
    });

    updateProject({
      content: { items: newItems },
    });
  }, [content?.items, selected, updateProject]);

  const onReset = useCallback(() => {
    setSearchMode(null);
    setSearchFilter("");
  }, [setSearchFilter, setSearchMode]);

  const changeInput = useCallback(event => setSearchFilter(event.target.value), [setSearchFilter]);
  const eraseInput = useCallback(() => setSearchFilter(""), [setSearchFilter]);
  const debouncedSearchAll = useMemo(() => debounce(searchAll, DEBOUNCE_DELAY), [searchAll]);
  const searchRequest = useCallback(() => searchAll(searchFilter), [searchFilter, searchAll]);

  const options = [
    { text: t("searchInLayers"), value: SearchMode.Layers, icon: "layers" as IconTypesKeys },
    { text: t("searchInObjects"), value: SearchMode.Objects, icon: "points" as IconTypesKeys },
  ];

  const placeholder =
    searchMode === SearchMode.Layers
      ? t("searchInLayers")
      : searchMode === SearchMode.Objects
      ? t("searchInObjects")
      : t("search");

  const isFixedExpandButton = isFocused || showOptions || !!searchMode || !!searchFilter;

  const showLayerControls = !searchMode && projectPermissions !== Permissions.Read;

  useEffect(() => {
    if (searchMode === SearchMode.Objects) {
      debouncedSearchAll(searchFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFilter, searchMode]);

  useEffect(() => {
    if (searchMode && searchMode !== SearchMode.Selection) {
      inputRef.current.focus();
    }
  }, [searchMode]);

  return (
    <>
      <Header $isFixedExpandButton={isFixedExpandButton}>
        <SearchMenu>
          <SearchButton
            kind={getSearchIcon(searchMode)}
            primary={!!searchMode && searchMode !== SearchMode.Selection}
            disabled={!searchMode || searchMode === SearchMode.Selection}
            onClick={onReset}
          />
          <Popup
            isVisible={showOptions}
            placement="left-start"
            distance={-5}
            onRequestClose={toggleOptions}
            hideOnClick
            anchor={ref => <ExpandButton innerRef={ref} title={t("search")} onClick={toggleOptions} />}
          >
            <SearchMenuContainer>
              <Menu options={options} selectOption={selectOption} />
            </SearchMenuContainer>
          </Popup>
        </SearchMenu>
        <SearchField
          innerRef={inputRef}
          onEraseClick={eraseInput}
          onChange={changeInput}
          onSearchRequest={searchRequest}
          onFocus={onFocus}
          onBlur={onBlur}
          value={searchFilter}
          width={"100%"}
          placeholder={`${placeholder}...`}
          withSearchIcon={false}
        />
        {!isShared && (!searchMode || searchMode === SearchMode.Selection) && (
          <SelectionButton
            title={t("selectLayers")}
            primary={searchMode === SearchMode.Selection}
            onClick={toggleSelectionMode}
          />
        )}
        {!isShared && showLayerControls && (
          <AddLayer Button={AddLayerButton} popupProps={{ offset: [46, 0], placement: "bottom-start" }} />
        )}
        {searchMode === SearchMode.Selection && <DeleteButton disabled={!selected.length} onClick={onDelete} />}
      </Header>
      {searchFilter && searchMode === SearchMode.Objects && <LayersListSearchResults />}
    </>
  );
};
